import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line no-unused-vars
import { AnimatePresence, motion, LayoutGroup } from 'framer-motion'
import {
    FaHome,
    FaFileAlt,
    FaEnvelope,
    FaUserShield,
    FaDraftingCompass,
    FaHardHat,
    FaHome as FaRoofing,
    FaWindowMaximize
} from 'react-icons/fa'

/**
 * Detects if the app is running as an installed PWA (standalone mode).
 * Works on iOS (navigator.standalone) and Android/Chrome (display-mode media query).
 */
function useIsStandalone() {
    const [standalone, setStandalone] = useState(() => {
        if (typeof window === 'undefined') return false
        if (window.navigator.standalone === true) return true
        if (window.matchMedia('(display-mode: standalone)').matches) return true
        return false
    })

    useEffect(() => {
        const mql = window.matchMedia('(display-mode: standalone)')
        const handler = (e) => setStandalone(e.matches || window.navigator.standalone === true)
        mql.addEventListener('change', handler)
        return () => mql.removeEventListener('change', handler)
    }, [])

    return standalone
}

/**
 * Returns true when the user is actively scrolling; reverts to false after 1.5s idle.
 */
function useScrollCompact() {
    const [compact, setCompact] = useState(false)
    const lastY = useRef(0)
    const idleTimer = useRef(null)

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY
            if (Math.abs(y - lastY.current) > 4) setCompact(true)
            lastY.current = y
            clearTimeout(idleTimer.current)
            idleTimer.current = setTimeout(() => setCompact(false), 1500)
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', onScroll)
            clearTimeout(idleTimer.current)
        }
    }, [])

    return compact
}

const FORM_OPTIONS = [
    {
        key: 'architecture',
        icon: FaDraftingCompass,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        path: '/forms/architecture/new-quote'
    },
    {
        key: 'construction',
        icon: FaHardHat,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        path: '/forms/construction/new-quote'
    },
    {
        key: 'roofing',
        icon: FaRoofing,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        path: '/forms/roofing/new-quote'
    },
    {
        key: 'impact_windows',
        icon: FaWindowMaximize,
        color: 'text-cyan-600',
        bg: 'bg-cyan-50',
        path: '/forms/impact-windows/new-quote'
    }
]

const SPRING = { type: 'spring', damping: 28, stiffness: 400 }

const PWABottomNav = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const isStandalone = useIsStandalone()
    const compact = useScrollCompact()
    const [showFormPicker, setShowFormPicker] = useState(false)
    const pickerRef = useRef(null)

    // Drag / edge positioning
    const EDGE_KEY = 'pwa_nav_edge'
    const [edge, setEdge] = useState(() => {
        if (typeof window === 'undefined') return 'bottom'
        return sessionStorage.getItem(EDGE_KEY) || 'bottom'
    })
    const [dragState, setDragState] = useState('idle') // idle | pressing | dragging | snapping
    const dragRef = useRef({ startX: 0, startY: 0, currentX: 0, currentY: 0, timer: null })
    const navRef = useRef(null)
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
    const [previewEdge, setPreviewEdge] = useState('bottom')
    const LONG_PRESS_MS = 500
    const MOVE_THRESHOLD = 10

    // Write edge to sessionStorage whenever it changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(EDGE_KEY, edge)
        }
    }, [edge])

    // Lock body scroll while dragging the nav
    useEffect(() => {
        if (dragState === 'dragging') {
            const original = document.body.style.overflow
            document.body.style.overflow = 'hidden'
            return () => { document.body.style.overflow = original }
        }
    }, [dragState])

    // Close picker on outside tap
    useEffect(() => {
        if (!showFormPicker) return
        const handler = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowFormPicker(false)
            }
        }
        document.addEventListener('pointerdown', handler)
        return () => document.removeEventListener('pointerdown', handler)
    }, [showFormPicker])

    // Close picker on navigation
    useEffect(() => {
        setShowFormPicker(false)
    }, [location.pathname])

    // Drag detection helpers
    const startDrag = () => {
        setDragState('dragging')
        setShowFormPicker(false)
    }

    const computeEdge = (x, y) => {
        const w = window.innerWidth
        const h = window.innerHeight
        const cx = w / 2
        const cy = h / 2
        const dx = Math.abs(x - cx)
        const dy = Math.abs(y - cy)
        if (dx > dy) {
            return x < cx ? 'left' : 'right'
        }
        return y < cy ? 'top' : 'bottom'
    }

    const snapToEdge = (x, y) => {
        const newEdge = computeEdge(x, y)
        setDragState('snapping')
        setEdge(newEdge)
        setTimeout(() => setDragState('idle'), 300)
    }

    const onPointerDown = (e) => {
        if (dragState !== 'idle') return
        const d = dragRef.current
        d.startX = e.clientX
        d.startY = e.clientY
        d.currentX = e.clientX
        d.currentY = e.clientY
        setDragState('pressing')
        d.timer = setTimeout(() => {
            if (dragRef.current.timer) {
                startDrag()
            }
        }, LONG_PRESS_MS)
    }

    const onPointerMove = (e) => {
        const d = dragRef.current
        if (dragState === 'pressing') {
            const dist = Math.hypot(e.clientX - d.startX, e.clientY - d.startY)
            if (dist > MOVE_THRESHOLD) {
                clearTimeout(d.timer)
                d.timer = null
                setDragState('idle')
            }
        } else if (dragState === 'dragging') {
            d.currentX = e.clientX
            d.currentY = e.clientY
            setPreviewEdge(computeEdge(e.clientX, e.clientY))

            // Viewport-clamped drag offset
            const nav = navRef.current
            const vw = window.innerWidth
            const vh = window.innerHeight
            const navW = nav ? nav.offsetWidth : 180
            const navH = nav ? nav.offsetHeight : 64
            const halfW = navW / 2
            const halfH = navH / 2

            const targetX = e.clientX - vw / 2
            const targetY = e.clientY - vh / 2
            const clampX = Math.max(-(vw / 2) + halfW, Math.min((vw / 2) - halfW, targetX))
            const clampY = Math.max(-(vh / 2) + halfH, Math.min((vh / 2) - halfH, targetY))
            setDragOffset({ x: clampX, y: clampY })
        }
    }

    const onPointerUp = (e) => {
        const d = dragRef.current
        if (dragState === 'pressing') {
            clearTimeout(d.timer)
            d.timer = null
            setDragState('idle')
            // normal tap — let the click handler on buttons fire
        } else if (dragState === 'dragging') {
            setDragOffset({ x: 0, y: 0 })
            snapToEdge(e.clientX, e.clientY)
        }
    }

    // Window-level move/up so drag works even if finger leaves the nav
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (dragState !== 'pressing' && dragState !== 'dragging') return
        const move = (e) => onPointerMove(e)
        const up = (e) => onPointerUp(e)
        window.addEventListener('pointermove', move)
        window.addEventListener('pointerup', up)
        return () => {
            window.removeEventListener('pointermove', move)
            window.removeEventListener('pointerup', up)
        }
    }, [dragState])
    /* eslint-enable react-hooks/exhaustive-deps */

    // Only render in installed PWA mode
    if (!isStandalone) return null

    const path = location.pathname

    const isActive = (check) => {
        if (check === '/') return path === '/' || path === '/home'
        return path.startsWith(check)
    }

    const tabs = [
        { id: 'home', icon: FaHome, label: t('nav.home', 'Home'), path: '/' },
        { id: 'forms', icon: FaFileAlt, label: t('nav.quote', 'Quote'), action: 'picker' },
        { id: 'contact', icon: FaEnvelope, label: t('nav.contact', 'Contact'), path: '/contact' },
        { id: 'admin', icon: FaUserShield, label: t('nav.user', 'User'), path: '/admin' }
    ]

    const isVertical = edge === 'left' || edge === 'right'

    const pickerStyle = (() => {
        const sb = 'env(safe-area-inset-bottom, 0px)'
        const st = 'env(safe-area-inset-top, 0px)'
        const sl = 'env(safe-area-inset-left, 0px)'
        const sr = 'env(safe-area-inset-right, 0px)'
        switch (edge) {
            case 'bottom':
                return { bottom: `calc(${sb} + 5rem)`, left: `max(0.75rem, ${sl})`, right: `max(0.75rem, ${sr})` }
            case 'top':
                return { top: `calc(${st} + 5rem)`, left: `max(0.75rem, ${sl})`, right: `max(0.75rem, ${sr})` }
            case 'left':
                return { left: `calc(${sl} + 5rem)`, top: `max(0.75rem, ${st})`, bottom: `max(0.75rem, ${sb})`, maxWidth: '16rem' }
            case 'right':
                return { right: `calc(${sr} + 5rem)`, top: `max(0.75rem, ${st})`, bottom: `max(0.75rem, ${sb})`, maxWidth: '16rem' }
        }
    })()

    const containerClass = isVertical
        ? 'fixed inset-y-0 z-[9999] flex items-center pointer-events-none'
        : 'fixed inset-x-0 z-[9999] flex justify-center pointer-events-none'

    const containerStyle = (() => {
        const sb = 'env(safe-area-inset-bottom, 0px)'
        const st = 'env(safe-area-inset-top, 0px)'
        const sl = 'env(safe-area-inset-left, 0px)'
        const sr = 'env(safe-area-inset-right, 0px)'
        switch (edge) {
            case 'bottom': return { bottom: `calc(${sb} + 0.75rem)` }
            case 'top':    return { top: `calc(${st} + 0.75rem)` }
            case 'left':   return { left: `calc(${sl} + 0.75rem)` }
            case 'right':  return { right: `calc(${sr} + 0.75rem)` }
        }
    })()

    return (
        <>
            {/* Form picker popover */}
            <AnimatePresence>
                {showFormPicker && (
                    <motion.div
                        ref={pickerRef}
                        initial={{ opacity: 0, scale: 0.95, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 12 }}
                        transition={SPRING}
                        className={`fixed z-[9998] ${edge === 'bottom' || edge === 'top' ? 'max-w-xs mx-auto' : 'max-w-[16rem]'}`}
                        style={pickerStyle}
                    >
                        <div className="overflow-hidden bg-white/90 backdrop-blur-2xl rounded-3xl border border-white/60 shadow-2xl shadow-black/10 ring-1 ring-black/5">
                            <div className="px-4 pt-3.5 pb-2">
                                <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">
                                    {t('nav.select_form', 'Select Form')}
                                </p>
                            </div>
                            {FORM_OPTIONS.map((opt) => (
                                <button
                                    key={opt.key}
                                    onClick={() => {
                                        navigate(opt.path)
                                        setShowFormPicker(false)
                                    }}
                                    className="flex items-center gap-3 w-full px-4 py-3 hover:bg-gray-50/80 active:bg-gray-100/80 transition-colors"
                                >
                                    <span className={`flex items-center justify-center w-9 h-9 rounded-2xl ${opt.bg}`}>
                                        <opt.icon className={`text-base ${opt.color}`} />
                                    </span>
                                    <span className="text-sm font-semibold text-gray-700">
                                        {t(`nav.form_${opt.key}`, opt.key.charAt(0).toUpperCase() + opt.key.slice(1).replace('_', ' '))}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Ghost previews of all 4 edge positions */}
            <AnimatePresence>
                {dragState === 'dragging' && (
                    <>
                        {(['bottom', 'top', 'left', 'right']).map((gEdge) => {
                            const gVertical = gEdge === 'left' || gEdge === 'right'
                            const gContainerClass = gVertical
                                ? 'fixed inset-y-0 z-[9997] flex items-center pointer-events-none'
                                : 'fixed inset-x-0 z-[9997] flex justify-center pointer-events-none'
                            const gContainerStyle = (() => {
                                const sb = 'env(safe-area-inset-bottom, 0px)'
                                const st = 'env(safe-area-inset-top, 0px)'
                                const sl = 'env(safe-area-inset-left, 0px)'
                                const sr = 'env(safe-area-inset-right, 0px)'
                                switch (gEdge) {
                                    case 'bottom': return { bottom: `calc(${sb} + 0.75rem)` }
                                    case 'top':    return { top: `calc(${st} + 0.75rem)` }
                                    case 'left':   return { left: `calc(${sl} + 0.75rem)` }
                                    case 'right':  return { right: `calc(${sr} + 0.75rem)` }
                                }
                            })()
                            const isNearest = previewEdge === gEdge
                            return (
                                <motion.div
                                    key={gEdge}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className={gContainerClass}
                                    style={gContainerStyle}
                                >
                                    <div
                                        className={`flex gap-0.5 bg-white/40 backdrop-blur-2xl rounded-[2.5rem] border border-white/50 shadow-xl shadow-black/[0.06] ring-1 ring-black/[0.03] overflow-hidden grayscale ${gVertical ? 'flex-col px-1.5 sm:px-2 py-2 sm:py-2.5' : 'flex-row px-2 sm:px-2.5 py-2 sm:py-2.5'}`}
                                        style={gVertical ? { maxHeight: 'calc(100vh - 2rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' } : undefined}
                                    >
                                        {tabs.map((tab) => (
                                            <div
                                                key={tab.id}
                                                className={`relative flex flex-col items-center justify-center rounded-[2rem] select-none ${gVertical ? 'px-1.5 sm:px-2 py-2.5 sm:py-3' : 'px-2.5 sm:px-3.5 py-1.5 sm:py-2'}`}
                                            >
                                                <tab.icon className={`text-sm sm:text-[1.1rem] text-gray-400/60`} />
                                            </div>
                                        ))}
                                        {isNearest && (
                                            <div className="absolute inset-0 rounded-[2.5rem] border-2 border-primary/30 pointer-events-none" />
                                        )}
                                    </div>
                                </motion.div>
                            )
                        })}
                    </>
                )}
            </AnimatePresence>

            {/* Floating pill nav */}
            <div className={containerClass} style={containerStyle}>
                <LayoutGroup>
                    <motion.nav
                        ref={navRef}
                        layout={dragState !== 'dragging'}
                        transition={SPRING}
                        onPointerDown={onPointerDown}
                        className={`pointer-events-auto flex gap-0.5 bg-white/75 backdrop-blur-2xl rounded-[2.5rem] border border-white/70 shadow-2xl shadow-black/[0.09] ring-1 ring-black/[0.04] overflow-hidden ${isVertical ? 'flex-col px-1.5 sm:px-2 py-2 sm:py-2.5' : 'flex-row px-2 sm:px-2.5 py-2 sm:py-2.5'}`}
                        style={isVertical ? { maxHeight: 'calc(100vh - 2rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' } : undefined}
                        animate={{
                            x: dragState === 'dragging' ? dragOffset.x : 0,
                            y: dragState === 'dragging' ? dragOffset.y : 0,
                            scale: dragState === 'dragging' ? 1.08 : 1,
                            opacity: dragState === 'dragging' ? 0.95 : 1,
                        }}
                    >
                        {/* Drag indicator */}
                        {dragState === 'dragging' && (
                            <div className="absolute inset-0 rounded-[2.5rem] border-2 border-primary/40 pointer-events-none z-20" />
                        )}

                        {tabs.map((tab) => {
                            const active = tab.id === 'forms'
                                ? (path.startsWith('/forms') || showFormPicker)
                                : isActive(tab.path)

                            return (
                                <motion.button
                                    layout
                                    key={tab.id}
                                    onClick={() => {
                                        if (tab.action === 'picker') {
                                            setShowFormPicker(prev => !prev)
                                        } else {
                                            setShowFormPicker(false)
                                            navigate(tab.path)
                                        }
                                    }}
                                    className={`relative flex flex-col items-center justify-center rounded-[2rem] transition-colors select-none ${isVertical ? 'px-1.5 sm:px-2 py-2.5 sm:py-3' : 'px-2.5 sm:px-3.5 py-1.5 sm:py-2'}`}
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                    {/* Sliding active background pill */}
                                    {active && (
                                        <motion.span
                                            layoutId="active-pill"
                                            className="absolute inset-0 bg-primary/[0.10] rounded-[2rem]"
                                            transition={SPRING}
                                        />
                                    )}

                                    {/* Icon */}
                                    <motion.span layout className="relative z-10">
                                        <tab.icon
                                            className={`transition-colors ${compact ? 'text-sm sm:text-[1.15rem]' : 'text-sm sm:text-[1.1rem]'} ${active ? 'text-primary' : 'text-gray-500/80'}`}
                                        />
                                    </motion.span>

                                    {/* Label — fades + collapses when compact */}
                                    <AnimatePresence initial={false}>
                                        {!compact && (
                                            <motion.span
                                                key="label"
                                                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                                animate={{ opacity: 1, height: 'auto', marginTop: 3 }}
                                                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                                transition={{ duration: 0.18, ease: 'easeInOut' }}
                                                className={`relative z-10 block text-[10px] font-semibold leading-none whitespace-nowrap overflow-hidden transition-colors ${active ? 'text-primary' : 'text-gray-500/80'}`}
                                            >
                                                {tab.label}
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                </motion.button>
                            )
                        })}
                    </motion.nav>
                </LayoutGroup>
            </div>
        </>
    )
}

export default PWABottomNav
